/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Component,
  computed,
  ElementRef,
  forwardRef,
  input,
  ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InputFileAcceptedFiles } from './interfaces/input-file-accepted-files.interface';

@Component({
  selector: 'app-input-file',
  templateUrl: './input-file.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputFileComponent),
      multi: true,
    },
  ],
  imports: [FontAwesomeModule],
})
export class InputFileComponent implements ControlValueAccessor {
  label = input('');
  formControlName = input.required<string>();
  @ViewChild('fileInput') fileInputElement!: ElementRef<HTMLInputElement>;
  placeholder = input('Choose a file');
  faXmark = faXmark;
  acceptedFileTypes = input.required<InputFileAcceptedFiles>();

  accept = computed(() => {
    const types = this.acceptedFileTypes();
    return Object.entries(types)
      .filter(([_, value]) => value)
      .map(([key]) => `.${key}`)
      .join(',');
  });

  private _selectedFiles: File | null = null;
  private _isDisabledState = false;

  get fileInputId() {
    return `file_input_${this.formControlName()}`;
  }

  get hasFiles(): boolean {
    return this._selectedFiles !== null;
  }

  get fileDisplayName(): string {
    if (!this._selectedFiles) return '';

    if (this._selectedFiles instanceof FileList) {
      if (this._selectedFiles.length === 1) {
        return this._selectedFiles[0].name;
      }
      return `${this._selectedFiles.length} files selected`;
    }

    return this._selectedFiles.name;
  }

  get isDisabled() {
    return this._isDisabledState;
  }

  // ControlValueAccessor callback functions
  private onChange = (value: File | null) => {};
  private onTouched = () => {};

  onFileChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;

    if (files && files.length > 0) {
      this._selectedFiles = files[0];
      this.onChange(this._selectedFiles);
    } else {
      this._selectedFiles = null;
      this.onChange(null);
    }
  }

  onBlur(): void {
    this.onTouched();
  }

  clearFiles(): void {
    this._selectedFiles = null;
    this.onChange(null);

    if (this.fileInputElement) {
      this.fileInputElement.nativeElement.value = '';
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: File | null): void {
    this._selectedFiles = value || null;
    // Ensure the native input value is cleared when programmatically resetting.
    // This allows selecting the same file again to trigger a change event.
    if (!value && this.fileInputElement) {
      this.fileInputElement.nativeElement.value = '';
    }
  }

  registerOnChange(fn: (value: File | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._isDisabledState = isDisabled;
  }
}
